/**
 * Shared utility functions for Registration components
 */

/**
 * Validates if a string is a properly formatted email address
 * @param {string} email Email address to validate
 * @returns {boolean} Boolean indicating if the email is valid
 */
export const isValidEmail = email => {
	return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

/**
 * Validates if a password meets minimum length requirements
 * @param {string} password Password to validate
 * @param {number} minLength Minimum required length (default: 8)
 * @returns {boolean} Boolean indicating if the password is valid
 */
export const isValidPassword = (password, minLength = 8) => {
	return password.length >= minLength;
};

/**
 * Checks if two passwords match
 * @param {string} password Primary password
 * @param {string} confirmPassword Confirmation password
 * @returns {boolean} Boolean indicating if passwords match
 */
export const passwordsMatch = (password, confirmPassword) => {
	return password === confirmPassword;
};

/**
 * Normalizes an email address by converting to lowercase and trimming whitespace
 * @param {string} email Email address to normalize
 * @returns {string} Normalized email address
 */
export const normalizeEmail = email => {
	return email.toLowerCase().trim();
};

/**
 * Formats a phone number as (XXX) XXX-XXXX
 * @param {string} input Raw phone number input
 * @returns {string} Formatted phone number
 */
export const formatPhoneNumber = input => {
	// Remove all non-numeric characters
	const cleaned = input.replace(/\D/g, "");

	// Format as (XXX) XXX-XXXX
	if (cleaned.length <= 3) {
		return cleaned;
	} else if (cleaned.length <= 6) {
		return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
	} else {
		return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(
			6,
			10
		)}`;
	}
};

/**
 * Formats a phone number for API submission (E.164 format)
 * @param {string} phoneNumber Phone number to format
 * @returns {string} E.164 formatted phone number (e.g., +11234567890)
 */
export const formatPhoneForApi = phoneNumber => {
	const cleanedPhone = phoneNumber.replace(/\D/g, "");
	return cleanedPhone.length === 10 ? `+1${cleanedPhone}` : cleanedPhone;
};

/**
 * Extract message from format: ('message', -1) -> message
 * @param {string} message Message to extract from
 * @returns {string} Extracted message
 */
export const formatMessage = message => {
	const match = message.match(/\('([^']+)'/);
	if (match) {
		return match[1];
	} else {
		return message;
	}
};
